import React, { useEffect, useState } from 'react';
import { ArrowRight, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { sportTypeAPI } from '../../../services/api';
import { TEAM_ROSTER_MIN, TEAM_ROSTER_MAX, TEAM_ROSTER_DEFAULT } from '../../data/participantConstants';
import ParticipantTypeToggle, { PARTICIPANT_COUNT_QUESTION, PARTICIPANT_COUNT_HINT } from '../ParticipantTypeToggle';
import Stepper from '../Stepper';
import WizardFrame from './WizardFrame';

// Same fallback list TournamentForm.jsx uses if the API call fails — keeps
// this demo screen usable even offline/API-down, which matters more here
// than anywhere else in the app since its whole point is a no-dependency demo.
// standardMinPlayerPerSide mirrors what these sports are actually seeded
// with (see exsports_fastify_apis' sports_types table).
const FALLBACK_SPORT_TYPES = [
  { id: 18, sportName: 'Basketball', standardMinPlayerPerSide: 5, playedBy: 'team' },
  { id: 21, sportName: 'Cricket', standardMinPlayerPerSide: 11, playedBy: 'team' },
  { id: 20, sportName: 'Pickleball', standardMinPlayerPerSide: 1, playedBy: 'individual' },
];

/**
 * Wizard step 0 of 5 — ONLY reached in standalone mode (Explore Tournament
 * Formats, opened from the Tournaments list rather than a real tournament).
 * Collects the same basic facts a real tournament would already have —
 * sport, roughly how many participants, singles/doubles/teams — entirely by
 * hand, since there's no real tournament to read them from. Skipped
 * entirely when this wizard is opened from inside a real tournament's
 * Schedule tab (see useScheduleBuilder's `tournamentContext`).
 *
 * `maxParticipants` (see useScheduleBuilder's `maxParticipantsAvailable`) is
 * this step's only window into how many participants actually exist to pick
 * from — it never imports a data source itself, so it has no idea whether
 * that ceiling comes from a mock pool or (once built) real enrollment data.
 */
const TestDataStep = ({ config, updateConfig, proceedFromTestData, maxParticipants }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [sportTypes, setSportTypes] = useState(FALLBACK_SPORT_TYPES);
  const { sportType, participantType, participantCount, playersPerTeam } = config;

  useEffect(() => {
    let cancelled = false;
    sportTypeAPI.getAll()
      .then((data) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) setSportTypes(data);
      })
      .catch(() => {
        // FALLBACK_SPORT_TYPES is already the initial state — nothing to do
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canProceed = !!sportType && !!participantCount && participantCount > 0;

  // The sport's own real minimum team size (e.g. 1 for a singles-capable
  // sport, 11 for cricket) — falls back to the generic default for a sport
  // that doesn't have this seeded (or none selected yet). Only applied at
  // the moment sport or game type changes to 'teams'; the roster Stepper
  // itself is a free-standing value afterward, same as every other config
  // field, so the organizer's own adjustment always wins from then on.
  const defaultPlayersPerTeam = (sportName) => sportTypes.find((s) => s.sportName === sportName)?.standardMinPlayerPerSide ?? TEAM_ROSTER_DEFAULT;

  // Sports seeded played_by: 'team' (cricket, soccer, volleyball, basketball)
  // have no real singles/doubles form — Game type is locked to Teams for them.
  const isTeamOnlySport = (sportName) => sportTypes.find((s) => s.sportName === sportName)?.playedBy?.toLowerCase() === 'team';
  const disabledParticipantTypes = isTeamOnlySport(sportType) ? ['singles', 'doubles'] : [];

  return (
    <WizardFrame
      title="Try a tournament format"
      subtitle="Explore any schedule format on realistic mock data — no tournament required. Set the basics once here; everything after this adapts to your choices, same as it would for a real tournament."
      footer={
        <>
          <button
            type="button"
            onClick={() => navigate('/tournaments')}
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${theme.buttonGhost}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={proceedFromTestData}
            disabled={!canProceed}
            className={`flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all ${theme.shadow} ${
              canProceed ? `${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]` : `${theme.buttonGhost} cursor-not-allowed opacity-50`
            }`}
          >
            Next
            <ArrowRight size={16} />
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={`mb-1.5 block text-sm font-medium ${theme.text}`}>Sport</label>
          <select
            value={sportType || ''}
            onChange={(e) => {
              const newSportType = e.target.value || null;
              const newSportPlayedBy = sportTypes.find((s) => s.sportName === newSportType)?.playedBy?.toLowerCase() ?? null;
              const nextParticipantType = newSportPlayedBy === 'team' ? 'teams' : participantType;
              updateConfig({
                sportType: newSportType,
                sportPlayedBy: newSportPlayedBy,
                participantType: nextParticipantType,
                ...(nextParticipantType === 'teams' ? { playersPerTeam: defaultPlayersPerTeam(newSportType) } : {}),
              });
            }}
            className={`w-full max-w-xs rounded-lg border px-3 py-2 text-sm ${theme.input}`}
          >
            <option value="" disabled>
              Select a sport
            </option>
            {sportTypes.map((sport) => (
              <option key={sport.id} value={sport.sportName}>
                {sport.sportName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={`mb-1.5 block text-sm font-medium ${theme.text}`}>Game type</label>
          <ParticipantTypeToggle
            value={participantType}
            disabledOptions={disabledParticipantTypes}
            onChange={(v) =>
              updateConfig({
                participantType: v,
                ...(v === 'teams' ? { playersPerTeam: defaultPlayersPerTeam(sportType) } : {}),
              })
            }
          />
        </div>

        <div>
          <label className={`mb-1.5 block text-sm font-medium ${theme.text}`}>{PARTICIPANT_COUNT_QUESTION[participantType]}</label>
          <input
            type="number"
            min={2}
            max={maxParticipants}
            value={participantCount ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return updateConfig({ participantCount: null });
              const n = Math.max(0, Math.min(maxParticipants, parseInt(raw, 10) || 0));
              updateConfig({ participantCount: n });
            }}
            className={`w-full max-w-xs rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            placeholder={`2–${maxParticipants}`}
          />
          {PARTICIPANT_COUNT_HINT[participantType] && (
            <p className={`mt-1.5 text-xs ${theme.textMuted}`}>{PARTICIPANT_COUNT_HINT[participantType]}</p>
          )}
          <p className={`mt-1.5 text-xs ${theme.textMuted}`}>
            {participantType === 'teams'
              ? "Every format has its own minimum number of teams — you'll still be able to adjust this after picking one."
              : "Just a starting point — every format has its own minimum, and you can fine-tune the exact count once you've picked one."}
          </p>
        </div>

        {participantType === 'teams' && (
          <div className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-4`}>
            <div className={`mb-3 flex items-center gap-2 text-sm font-semibold ${theme.text}`}>
              <Users size={16} />
              Team roster
            </div>
            <Stepper
              label="Players per team"
              hint="MLP-style pairing settings only apply once each team has a full 4-player roster."
              value={playersPerTeam}
              min={TEAM_ROSTER_MIN}
              max={TEAM_ROSTER_MAX}
              onChange={(v) => updateConfig({ playersPerTeam: v })}
            />
          </div>
        )}
      </div>
    </WizardFrame>
  );
};

export default TestDataStep;
